import type {
    EditableTextElement,
    EditableTextKeyCodes,
    EditableTextCommonEvent,
    EditableTextValue,
    MergeTextCriteria,
    TrimClipboardCriteria,
} from './types';
import { countCharacters } from '@/hooks/useCharacterLimit';

interface CommonRegExpGroup {
    [key: string]: RegExp;
}

export const commonRegExp: CommonRegExpGroup = {
    lineBreaks: /\r?\n|\r/gm,
};

export const keyEventCode: EditableTextKeyCodes = {
    diverted: {
        enter: 'Enter',
        escape: 'Escape',
    },
    allowedSingle: {
        // alt: 'Alt',
        backspace: 'Backspace',
        // control: 'Control',
        delete: 'Delete',
        end: 'End',
        home: 'Home',
        pageUp: 'PageUp',
        pageDown: 'PageDown',
        tab: 'Tab',
        // shift: 'Shift',
        left: 'ArrowLeft',
        up: 'ArrowUp',
        right: 'ArrowRight',
        down: 'ArrowDown',
    },
    allowedCombo: {
        keyA: 'a',
        keyX: 'x',
        keyC: 'c',
        keyV: 'v',
        keyZ: 'z',
    },
};

export const allowedSingleKeys = Object.values(keyEventCode.allowedSingle);
export const allowedComboKeys = Object.values(keyEventCode.allowedCombo);

export function getCharacterCount(text?: EditableTextValue, limit?: number) {
    return limit ? countCharacters(text ?? '', { limit }) : undefined;
}

export function getTextFromEventTarget(event: EditableTextCommonEvent) {
    return event.currentTarget.textContent;
}

export function insertTextAtPosition({ primaryText, secondaryText, position = 0 }: MergeTextCriteria) {
    const endOfFirstSegment = typeof position === 'number' ? position : position.start;
    const startOfLastSegment = typeof position === 'number' ? position : position.end;

    const startText = primaryText.slice(0, endOfFirstSegment);
    const endText = primaryText.slice(startOfLastSegment);

    return `${startText}${secondaryText}${endText}`;
}

export function trimClipboardToFitLimit({
    currentInputText,
    clipboardText,
    characterLimit,
    selectionLength,
}: TrimClipboardCriteria) {
    const clipboardLength = clipboardText.length;
    const currentTotal = currentInputText.length - selectionLength + clipboardLength;

    if (currentTotal <= characterLimit) {
        return clipboardText;
    }

    const difference = currentTotal - characterLimit;

    return clipboardText.slice(0, clipboardLength - difference);
}

export function sanitizeInput(text: string, filter: RegExp) {
    return text.trim().replace(filter, '');
}

export function replaceEditableValue(
    element: EditableTextElement,
    newValue: EditableTextValue,
    existingSelection?: Selection | null,
) {
    const selection = existingSelection ?? window.getSelection();

    if (!selection) {
        return;
    }

    selection.selectAllChildren(element);
    selection.deleteFromDocument();

    if (newValue) {
        selection.getRangeAt(0).insertNode(document.createTextNode(newValue));
    }

    // Calling this causes bugs in Firefox.
    // selection.removeAllRanges();
}

export function escFirefoxBlurFix(element: EditableTextElement, newValue: EditableTextValue) {
    // TODO: We can remove this if ever the FF bug is fixed.
    // We should instead be able to call `handleBlur(event, true)`.
    replaceEditableValue(element, newValue);
    element.blur();
}

export function selectAllText(element: EditableTextElement) {
    const selection = window.getSelection();

    if (selection && element) {
        selection.selectAllChildren(element);
    }
}

export function setSelectionPosition(element: EditableTextElement, position = 0, existingSelection?: Selection | null) {
    const range = document.createRange();
    const selection = existingSelection ?? window.getSelection();

    if (!selection) {
        return;
    }

    range.setStart(element.childNodes[0], position);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
}

export function windowHasTextSelection() {
    const selection = window.getSelection();
    return Boolean(selection && selection.toString().length);
}
